import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Code, 
  ExternalLink,
  Filter,
  Users,
  Calendar,
  Wrench
} from 'lucide-react';
import { Dealer } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  
  h1 {
    margin: 0;
    font-size: 2rem;
    font-weight: 700;
    color: #1f2937;
  }
`;

const AddButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #ed2024 0%, #a01519 100%);
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const FiltersContainer = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 300px;
  padding: 10px 12px 10px 40px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
    border-color: #ed2024;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const SearchContainer = styled.div`
  position: relative;
  flex: 1;
  min-width: 300px;
  
  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    width: 18px;
    height: 18px;
  }
`;

const FilterSelect = styled.select`
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  background: white;
  
  &:focus {
    outline: none;
    border-color: #ed2024;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`;

const DealersTable = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 200px 150px 120px 300px;
  gap: 16px;
  padding: 16px 20px;
  background: #f9fafb;
  border-bottom: 1px solid #e5e7eb;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 200px 150px 120px 300px;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid #f3f4f6;
  align-items: center;
  transition: background 0.2s;
  
  &:hover {
    background: #f9fafb;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const DealerInfo = styled.div`
  .dealer-name {
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 4px;
  }
  
  .dealer-email {
    font-size: 0.875rem;
    color: #6b7280;
  }
`;

const ContactInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  
  .phone {
    margin-bottom: 2px;
  }
`;

const LocationInfo = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
`;

const StatusBadge = styled.span<{ status: 'active' | 'inactive' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${props => props.status === 'active' ? '#dcfce7' : '#fee2e2'};
  color: ${props => props.status === 'active' ? '#166534' : '#991b1b'};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ variant?: 'danger' | 'secondary' }>`
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  background: white;
  color: ${props => 
    props.variant === 'danger' ? '#dc2626' : 
    props.variant === 'secondary' ? '#6b7280' : '#374151'
  };
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background: ${props => 
      props.variant === 'danger' ? '#fef2f2' : 
      props.variant === 'secondary' ? '#f9fafb' : '#f3f4f6'
    };
    border-color: ${props => 
      props.variant === 'danger' ? '#fca5a5' : '#9ca3af'
    };
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px;
  
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #e5e7eb;
    border-top: 3px solid #ed2024;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
  
  h3 {
    margin: 0 0 8px;
    color: #374151;
  }
  
  p {
    margin: 0 0 20px;
  }
`;

const DealerList: React.FC = () => {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const [filteredDealers, setFilteredDealers] = useState<Dealer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');

  useEffect(() => {
    loadDealers();
  }, []);

  useEffect(() => {
    filterDealers();
  }, [dealers, searchTerm, statusFilter]);

  const loadDealers = async () => {
    try {
      const response = await apiService.getDealers();
      if (response.success && response.data) {
        setDealers(response.data);
      } else {
        toast.error('Failed to load dealers');
      }
    } catch (error) {
      console.error('Error loading dealers:', error);
      toast.error('Failed to load dealers');
    } finally {
      setIsLoading(false);
    }
  };

  const filterDealers = () => {
    let filtered = dealers;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(dealer => 
        dealer.name.toLowerCase().includes(term) ||
        dealer.email.toLowerCase().includes(term) ||
        dealer.city.toLowerCase().includes(term) ||
        dealer.state.toLowerCase().includes(term)
      );
    }

    // Filter by status
    if (statusFilter !== 'all') {
      filtered = filtered.filter(dealer => 
        statusFilter === 'active' ? dealer.isActive : !dealer.isActive
      );
    }

    setFilteredDealers(filtered);
  };

  const handleDeleteDealer = async (dealer: Dealer) => {
    if (!window.confirm(`Are you sure you want to deactivate ${dealer.name}?`)) {
      return;
    }

    try {
      const response = await apiService.deleteDealer(dealer.id);
      if (response.success) {
        toast.success('Dealer deactivated successfully');
        loadDealers();
      } else {
        toast.error('Failed to deactivate dealer');
      }
    } catch (error) {
      console.error('Error deactivating dealer:', error);
      toast.error('Failed to deactivate dealer');
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <DealersTable>
          <LoadingSpinner>
            <div className="spinner" />
          </LoadingSpinner>
        </DealersTable>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader>
        <h1>Dealers</h1>
        <AddButton to="/dealers/new">
          <Plus />
          Add New Dealer
        </AddButton>
      </PageHeader>

      <FiltersContainer>
        <SearchContainer>
          <Search />
          <SearchInput
            type="text"
            placeholder="Search dealers by name, email, city, or state..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchContainer>
        
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active Only</option>
          <option value="inactive">Inactive Only</option>
        </FilterSelect>
      </FiltersContainer>

      <DealersTable>
        {filteredDealers.length === 0 ? (
          <EmptyState>
            <h3>No dealers found</h3>
            <p>
              {dealers.length === 0 
                ? "You haven't added any dealers yet."
                : "No dealers match your current filters."
              }
            </p>
            {dealers.length === 0 && (
              <AddButton to="/dealers/new">
                <Plus />
                Add Your First Dealer
              </AddButton>
            )}
          </EmptyState>
        ) : (
          <>
            <TableHeader>
              <div>Dealer</div>
              <div>Contact</div>
              <div>Location</div>
              <div>Status</div>
              <div>Actions</div>
            </TableHeader>
            
            {filteredDealers.map((dealer) => (
              <TableRow key={dealer.id}>
                <DealerInfo>
                  <div className="dealer-name">{dealer.name}</div>
                  <div className="dealer-email">{dealer.email}</div>
                </DealerInfo>
                
                <ContactInfo>
                  <div className="phone">{dealer.phone}</div>
                  {dealer.website && (
                    <a 
                      href={dealer.website} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ color: '#ed2024', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      Website <ExternalLink size={12} />
                    </a>
                  )}
                </ContactInfo>
                
                <LocationInfo>
                  <div>{dealer.city}, {dealer.state}</div>
                  <div>{dealer.zipCode}</div>
                </LocationInfo>
                
                <StatusBadge status={dealer.isActive ? 'active' : 'inactive'}>
                  {dealer.isActive ? 'Active' : 'Inactive'}
                </StatusBadge>
                
                <ActionButtons>
                  <ActionButton
                    as={Link}
                    to={`/dealers/${dealer.id}/edit`}
                    title="Edit dealer"
                  >
                    <Edit />
                  </ActionButton>
                  
                  <ActionButton
                    as={Link}
                    to={`/calendar/${dealer.id}`}
                    title="View calendar"
                    variant="secondary"
                  >
                    <Calendar />
                  </ActionButton>
                  
                  <ActionButton
                    as={Link}
                    to={`/dealers/${dealer.id}/embed`}
                    title="View embed code"
                    variant="secondary"
                  >
                    <Code />
                  </ActionButton>
                  
                  <ActionButton
                    as={Link}
                    to={`/dealers/${dealer.id}/users`}
                    title="Manage users"
                    variant="secondary"
                  >
                    <Users />
                  </ActionButton>
                  
                  <ActionButton
                    as={Link}
                    to={`/dealers/${dealer.id}/services`}
                    title="Manage services"
                    variant="secondary"
                  >
                    <Wrench />
                  </ActionButton>
                  
                  <ActionButton
                    onClick={() => handleDeleteDealer(dealer)}
                    title="Deactivate dealer"
                    variant="danger"
                  >
                    <Trash2 />
                  </ActionButton>
                </ActionButtons>
              </TableRow>
            ))}
          </>
        )}
      </DealersTable>
    </PageContainer>
  );
};

export default DealerList;